import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import StatusChip from "../../components/StatusChip";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, DollarSign, CheckCircle2, ExternalLink } from "lucide-react";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
// Release C-2 (P1) — Open in Microsoft 365 Mail for the Deposits surface.
import OpenInMicrosoftMailButton from "../../design-system/microsoft365/OpenInMicrosoftMailButton";

export default function Deposits() {
  const [items, setItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contract_id: "", amount: "", currency: "USD" });
  const [markOpen, setMarkOpen] = useState(null);
  const [markForm, setMarkForm] = useState({ reason: "Payment received via check", external_ref: "" });
  const [busy, setBusy] = useState(false);
  const [proofId, setProofId] = useState(null);
  const [proofReload, setProofReload] = useState(0);
  const [shownLink, setShownLink] = useState(null); // B5: raw link exists only in this response, never stored
  const showLink = (id, url) => setShownLink({ id, url: `${window.location.origin}${url}` });
  const rotate = async (id) => {
    try { showLink(id, (await api.post(`/deposits/${id}/link`)).data.payment_link_url); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const [d, c] = await Promise.all([api.get("/deposits"), api.get("/contracts")]);
      setItems(d.data); setContracts(c.data.filter((x) => x.status === "fully_signed" || x.status === "sent_for_signature"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/deposits", { ...form, amount: parseFloat(form.amount) });
      showLink(r.data.id, r.data.payment_link_url);
      toast.success("Deposit request created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const markPaid = async (id) => {
    setBusy(true);
    try {
      await api.post(`/deposits/${id}/mark-paid-manual`, markForm);
      toast.success("Marked paid");
      setMarkOpen(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <AppShell
      title="Deposits & Payments"
      subtitle={`${items.length} deposit request(s) · online payments not enabled (Stripe externally blocked)`}
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="deposits-add-btn">
              <Plus className="w-4 h-4 mr-1" /> New deposit
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-erf-cream" data-testid="deposits-add-dialog">
            <DialogHeader><DialogTitle className="font-display">New deposit request</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label className="text-xs uppercase font-nav">Contract</Label>
                <Select value={form.contract_id} onValueChange={(v)=>setForm({...form, contract_id: v})}>
                  <SelectTrigger className="mt-1" data-testid="dep-contract"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {contracts.length === 0 && <div className="p-2 text-[11px] text-erf-dark/60">No sent/signed contracts yet.</div>}
                    {contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.id.slice(0,8)} · {c.status}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase font-nav">Amount (USD)</Label><Input required type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form, amount: e.target.value})} className="mt-1 font-mono" data-testid="dep-amount" /></div>
              <Button type="submit" disabled={busy} className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="dep-submit">{busy ? "Creating…" : "Create deposit request"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {items.length === 0 ? (
        <EmptyState icon={DollarSign} title="No deposit requests yet" subtitle="Create a deposit request for a signed contract. Stripe integration ships as Connected once credentials are added." />
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2 mono">Deposit</th>
                <th className="text-left px-3 py-2">Provider</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2 mono">Amount</th>
                <th className="text-left px-3 py-2 mono">Public link</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-erf-borderCream/60" data-testid={`dep-row-${d.id}`}>
                  <td className="px-3 py-2 mono text-[11px]">{d.id.slice(0,8)}</td>
                  <td className="px-3 py-2"><StatusChip status={d.provider === "stripe" ? "connected" : "externally_blocked"} label={d.provider === "stripe" ? d.provider : "No payment provider"} /></td>
                  <td className="px-3 py-2"><StatusChip status={d.status === "paid" ? "verified" : d.status === "pending" ? "pending" : "not_configured"} label={d.status} /></td>
                  <td className="px-3 py-2 mono text-right font-semibold">${Number(d.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 mono text-[10px] text-erf-dark/50">
                    {shownLink?.id === d.id ? (
                      <a href={shownLink.url} target="_blank" rel="noreferrer" className="text-erf-goldHover hover:underline inline-flex items-center gap-1 break-all" data-testid={`dep-public-link-${d.id}`}>
                        Copy now — shown once <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <button type="button" onClick={() => rotate(d.id)} className="min-h-[44px] underline text-erf-dark/70" data-testid={`dep-new-link-${d.id}`}>
                        New link (old one stops working)
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {/* FINAL-POLISH P2 — Row-level icon-only Open in Outlook (Owner/Admin). */}
                    <span className="inline-block align-middle mr-1">
                      <OpenInMicrosoftMailButton
                        surface="deposit_row"
                        recordType="deposit"
                        recordId={d.id}
                        iconOnly
                      />
                    </span>
                    {d.status !== "paid" && (
                      <Dialog open={markOpen === d.id} onOpenChange={(v) => setMarkOpen(v ? d.id : null)}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-8 text-emerald-700" data-testid={`dep-mark-paid-${d.id}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-erf-cream">
                          <DialogHeader><DialogTitle className="font-display">Mark deposit paid</DialogTitle></DialogHeader>
                          <div className="space-y-3">
                            <div><Label className="text-xs uppercase font-nav">Reason</Label><Input required value={markForm.reason} onChange={(e)=>setMarkForm({...markForm, reason: e.target.value})} className="mt-1" data-testid="dep-mark-reason" /></div>
                            <div><Label className="text-xs uppercase font-nav">External ref (check #, ACH id)</Label><Input value={markForm.external_ref} onChange={(e)=>setMarkForm({...markForm, external_ref: e.target.value})} className="mt-1 font-mono" data-testid="dep-mark-ref" /></div>
                            <Button onClick={() => markPaid(d.id)} disabled={busy} className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid={`dep-mark-submit-${d.id}`}>{busy ? "Saving…" : "Confirm paid"}</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
